import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import axios from 'axios'
import {UserModel} from './_models'
import {Privilege} from '../../apps/privilege/privileges-list/core/_models'
import {getPrivileges} from '../../apps/privilege/privileges-list/core/_requests'
import {getUserByToken, refreshToken} from './_requests'
import {SchoolModel} from '../../apps/school/schools-list/core/_models'
import {
  getCurrentUser,
  getPrivilege,
  getSchool,
  responseInterceptor,
  setCurrentUser,
  setPrivilege,
  setSchool,
} from './AuthHelpers'
import {getSchool as school_api} from './_requests'
import {QUERIES, WithChildren} from '../../../../_metronic/helpers'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {v2_get_request_api} from '../../../../api/get'
import {switchCompany} from '../../../../i18n'

export const storageKey = {
  token: 'token',
  refresh_token: 'refresh_token',
  school: 'school',
  admission_fields: 'admission-fields',
}

type AuthContextProps = {
  token: string | null
  refresh_token: string | null
  currentUser?: UserModel
  school?: SchoolModel
  saveSchool: (school?: SchoolModel) => void
  privilege?: Privilege[]
  login: (token: string, refresh_token: string) => void
  getPermission?: (
    model: string,
    permission: 'is_view' | 'is_edit' | 'is_delete' | 'is_add'
  ) => boolean
  logout: () => void
  refresh: () => void
}
const initialState = {
  token: localStorage.getItem(storageKey.token),
  refresh_token: localStorage.getItem(storageKey.refresh_token),
  currentUser: getCurrentUser(),
  school: getSchool(),
  privilege: getPrivilege(),
  login: (token: string, refresh_token: string) => {},
  logout: () => {},
  refresh: () => {},
  saveSchool: (school?: SchoolModel) => {},
}
const AuthContext = createContext<AuthContextProps>(initialState)

const AuthProvider = ({children}: {children: ReactNode}) => {
  const [state, setState] = useState(initialState)
  const login = (token: string, refresh_token: string) => {
    setState({...state, token, refresh_token})
    localStorage.setItem(storageKey.token, token)
    localStorage.setItem(storageKey.refresh_token, refresh_token)
  }
  const logout = useCallback(() => {
    setState({...initialState, token: null, refresh_token: null, currentUser: undefined})
    localStorage.clear()
  }, [])
  const refresh = useCallback(async () => {
    const refresh_token = localStorage.getItem(storageKey.refresh_token)
    const auth_token = localStorage.getItem(storageKey.token)
    if (auth_token && refresh_token) {
      try {
        const auth = await refreshToken(refresh_token)
        if (auth) login(auth.auth_token, auth.refresh_token)
        else logout()
      } catch (error) {
        console.error('Error refreshing token', error)
        logout()
      }
    }
    // eslint-disable-next-line
  }, [])
  useEffect(() => {
    axios.interceptors.response.clear()
    responseInterceptor(refresh, logout)
    // eslint-disable-next-line
  }, [])
  const saveSchool = (school?: SchoolModel) => {
    if (!school) {
      setState({...state, school: undefined})
      localStorage.removeItem(storageKey.school)
      return
    }
    setState({...state, school})
    setSchool(school)
  }
  const getPermission = (
    model: string,
    permission: 'is_view' | 'is_edit' | 'is_delete' | 'is_add'
  ) => {
    let res = false
    const result = state.privilege?.find((p) => p.operation?.operation_slug === model)
    if (result) res = result[permission || 'is_view'] || false
    return res
  }
  const didRequest = useRef(false)
  useEffect(() => {
    const requestUser = async () => {
      try {
        const token = localStorage.getItem(storageKey.token)
        if (!token) {
          logout()
          const school = await school_api()
          if (school)
            setState((prev) => {
              return {...prev, school}
            })
          return
        }
        if (!didRequest.current) {
          const currentUser = await getUserByToken()
          if (currentUser) {
            setState((prev) => {
              return {...prev, currentUser}
            })
            setCurrentUser(currentUser)
            switchCompany(currentUser.school_id, 'en')
          }
          const privilege = await getPrivileges()
          if (privilege) {
            setState((prev) => {
              return {...prev, privilege: privilege.entities}
            })
            setPrivilege(privilege.entities || [])
          }
          const school = await v2_get_request_api({queryKey: [QUERIES.SCHOOLS_LIST + '/by_url']})
          if (school)
            setState((prev) => {
              return {...prev, school}
            })
        }
      } catch (error) {
        if (!didRequest.current) logout()
      }
      return () => (didRequest.current = true)
    }
    requestUser()
  }, [state.token, logout])
  return (
    <AuthContext.Provider value={{...state, login, logout, refresh, saveSchool, getPermission}}>
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {school, saveSchool} = useAuth()
  const didRequest = useRef(false)
  const [isLoading, setIsLoading] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestSchool = async () => {
      try {
        if (!didRequest.current && !school) {
          const data = (await school_api()) as any
          if (data) {
            saveSchool(data)
            switchCompany(data.id, 'en')
          }
        }
      } catch (error) {
        saveSchool(undefined)
      } finally {
        const admission_fields = await v2_get_request_api({
          queryKey: [QUERIES.ADMISSION_FIELD_LIST],
        })
        if (admission_fields?.entities) {
          localStorage.setItem(
            QUERIES.ADMISSION_FIELD_LIST,
            JSON.stringify(admission_fields.entities || [])
          )
        }
        setIsLoading(false)
      }

      return () => (didRequest.current = true)
    }
    requestSchool()
    // eslint-disable-next-line
  }, [])
  return isLoading ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, useAuth, AuthInit}
